/**
 * Files the Builder creates, shown in the conversation (eddy, 2026-10-27):
 * every file it writes and every screenshot it takes appears under the step
 * that made it, instead of only as a folded "Wrote …" action row.
 *
 * A file shows once, at the step that first created it; writing it again
 * later does not repeat it (the window it opens always reads the newest
 * version). Pure, so every rule here is tested without a browser.
 */

import type { TranscriptItem } from './coding-events';

type ActivityItem = Extract<TranscriptItem, { kind: 'activity' }>;

/** The line a saved screenshot's result carries (`SCREENSHOT_SAVED_PREFIX` + `SCREENSHOTS_DIR` in `src/coding/browser-tools.ts`). */
const SAVED_SCREENSHOT = /Saved to (\.eddy-screenshots\/[A-Za-z0-9._-]+)/;

/** The file one successful step created, or null when it created none. */
export function createdFile(item: Pick<ActivityItem, 'tool' | 'input' | 'result' | 'resultContent'>): string | null {
  if (item.result !== 'ok') return null;
  const name = item.tool.replace(/^mcp__[^_]+__/, '');
  if (name === 'screenshot') return SAVED_SCREENSHOT.exec(item.resultContent)?.[1] ?? null;
  if (name !== 'write_file') return null;
  try {
    const input = JSON.parse(item.input || '{}') as { path?: unknown; append?: unknown };
    if (input.append === true) return null;
    const path = typeof input.path === 'string' ? input.path.trim().replace(/^\.\/+/, '') : '';
    return path || null;
  } catch {
    return null;
  }
}

/** For each step that FIRST created a file, that file — keyed by the step's seq. */
export function firstCreations(items: readonly TranscriptItem[]): Map<number, string> {
  const seen = new Set<string>();
  const out = new Map<number, string>();
  for (const item of items) {
    if (item.kind !== 'activity') continue;
    const path = createdFile(item);
    if (!path || seen.has(path)) continue;
    seen.add(path);
    out.set(item.seq, path);
  }
  return out;
}

/** The files a group of steps created, in order. */
export function createdIn(steps: readonly { seq: number }[], creations: ReadonlyMap<number, string>): string[] {
  const out: string[] = [];
  for (const step of steps) {
    const path = creations.get(step.seq);
    if (path) out.push(path);
  }
  return out;
}
